import React from 'react';
import { Star, Phone, Mail, Facebook, Instagram, MessageSquare, Shield, CheckCircle } from 'lucide-react';
import { Header, Footer, usePageMeta } from './SharedComponents';
import reviewData from '../reviews.json';

const TreeServiceLinktree = () => {
  usePageMeta(
    'Connect With Us | One Choice Tree Service',
    'Call, email, or follow One Choice Tree Service — Maryland tree removal, trimming, and 24/7 emergency service. Free quotes: (443) 208-0747.'
  );

  const socialLinks = [
    {
      name: "Call Now - Emergency Service",
      description: "24/7 tree emergencies & free estimates",
      icon: <Phone className="w-6 h-6" />,
      bgColor: "bg-green-600 hover:bg-green-700",
      url: "tel:+14432080747",
      priority: true
    },
    {
      name: "Google Reviews",
      description: "Read reviews & leave us a review",
      icon: <Star className="w-6 h-6" />,
      bgColor: "bg-blue-600 hover:bg-blue-700",
      url: "https://g.page/r/CVs_S3pReiPrEBM/review",
      priority: true
    },
    {
      name: "Get Free Quote",
      description: "Email us for a quick estimate",
      icon: <Mail className="w-6 h-6" />,
      bgColor: "bg-orange-600 hover:bg-orange-700",
      url: "mailto:contact@onechoicetreeservice.com?subject=Free Quote Request"
    },
    {
      name: "Facebook",
      description: "Follow for updates & tree care tips",
      icon: <Facebook className="w-6 h-6" />,
      bgColor: "bg-blue-800 hover:bg-blue-900",
      url: "https://www.facebook.com/onechoicetreeservice/"
    },
    {
      name: "Instagram",
      description: "See our latest work & before/afters",
      icon: <Instagram className="w-6 h-6" />,
      bgColor: "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700",
      url: "https://www.instagram.com/onechoicetreeservice/"
    },
    {
      name: "TikTok",
      description: "Tree tips & satisfying removals",
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43V7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.43z"/>
        </svg>
      ),
      bgColor: "bg-black hover:bg-gray-800",
      url: "https://www.tiktok.com/@onechoicetreeservice"
    },
    {
      name: "Nextdoor",
      description: "Connect on your neighborhood app",
      icon: <MessageSquare className="w-6 h-6" />,
      bgColor: "bg-green-700 hover:bg-green-800",
      url: "https://nextdoor.com/pages/one-choice-tree-service-brooklyn-md/?is=feed_author"
    }
  ];

  const LinkButton = ({ link }) => (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`block w-full ${link.bgColor} text-white rounded-2xl p-5 mb-4 transform transition-all duration-200 hover:scale-[1.02] shadow-lg ${
        link.priority ? 'ring-2 ring-yellow-300 ring-opacity-60' : ''
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
          {link.icon}
        </div>
        <div className="flex-1 text-left">
          <h3 className="font-bold text-lg mb-1">{link.name}</h3>
          <p className="text-sm opacity-90">{link.description}</p>
        </div>
      </div>
    </a>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header currentPage="links" />

      <section className="bg-gradient-to-br from-green-50 to-blue-50 pt-24 pb-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Page header */}
        <div className="text-center mb-8">
          <div className="w-28 h-28 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl border border-gray-100 p-4">
            <img
              src="/logo-mark.svg"
              alt="One Choice Tree Service Logo"
              className="w-full h-full object-contain"
            />
          </div>
          
          <h1 className="text-xl font-bold text-gray-900 mb-3">
            Connect With Us
          </h1>
          
          <div className="inline-flex items-center bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium mb-3">
            <CheckCircle className="w-3 h-3 mr-1" />
            Licensed & Insured in Maryland
          </div>
          
          {/* Trust Indicators */}
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600 mb-2">
            <div className="flex items-center space-x-1">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 fill-current" />
                ))}
              </div>
              <span className="font-medium">{reviewData.rating.toFixed(1)}/5</span>
            </div>
            <div className="flex items-center space-x-1">
              <Shield className="w-3 h-3 text-green-600" />
              <span>Fully Insured</span>
            </div>
          </div>
          
          <p className="text-gray-600 text-sm">
            Anne Arundel • Howard • Baltimore • Carroll counties
          </p>
        </div>

        {/* Links */}
        <div className="space-y-3">
          {socialLinks.map((link, index) => (
            <LinkButton key={index} link={link} />
          ))}
        </div>

      </div>
      </section>

      <Footer />
    </div>
  );
};

export default TreeServiceLinktree;